import { getAuthHeaders, authedFetch } from './authService';
const HOSTNAME = process.env.REACT_APP_API_HOSTNAME ?? 'http://localhost:8000';

const notifyServiceError = (msg: string) => {
  try { window.dispatchEvent(new CustomEvent('wualt:notification:error', { detail: { message: msg } })); } catch (e) { /* ignore */ }
};

export type AuditLogItem = {
  id: number;
  timestamp: string;
  actor?: string;
  actor_email?: string;
  action?: string;
  target_entity?: string;
  target_id?: string;
  target_name?: string | null;
  before?: any;
  after?: any;
  user_agent?: string | null;
};

export async function getAuditLogs(page?: number, pageSize?: number, from?: string, to?: string, q?: string, actorUserId?: string, actor?: string, entity?: string): Promise<{ logs: AuditLogItem[]; meta?: any }> {
  const params = new URLSearchParams();
  if (typeof page === 'number') params.append('page', String(page));
  if (typeof pageSize === 'number') params.append('page_size', String(pageSize));
  if (from) params.append('date_from', from);
  if (to) params.append('date_to', to);
  if (actorUserId) params.append('actor_user_id', actorUserId);
  else if (actor) params.append('actor', actor);
  else if (q) params.append('actor', q);
  if (entity) params.append('entity', entity);
  const qs = params.toString();
  const url = `${HOSTNAME.replace(/\/$/, '')}/adminportal/audit-log${qs ? `?${qs}` : ''}`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    const msg = `Failed to fetch audit logs: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const body = await res.json().catch(() => null);
  const rawLogs = body?.data?.logs ?? body?.logs ?? [];
  // Normalize common field name variations and nested payloads so the UI can rely on consistent keys
  const logs = (Array.isArray(rawLogs) ? rawLogs : []).map((item: any) => {
    const pick = (obj: any, ...candidates: Array<string | number | symbol>): any => {
      for (const k of candidates) {
        if (obj == null) continue;
        if (Object.prototype.hasOwnProperty.call(obj, k)) return obj[k];
      }
      return undefined;
    };

    const normalized: any = { ...item };
    const container = item?.data ?? item?.payload ?? item?.details ?? item;

    normalized.timestamp =
      pick(item, 'timestamp', 'created_at', 'createdAt', 'time', 'ts') ??
      pick(container, 'timestamp', 'created_at', 'createdAt', 'time', 'ts') ??
      item.timestamp ??
      null;

    normalized.actor = pick(item, 'actor', 'user', 'performed_by', 'performedBy') ?? pick(container, 'actor', 'user') ?? item.actor ?? null;
    normalized.actor_email = pick(item, 'actor_email') ?? pick(container, 'actor_email') ?? null;

    normalized.action = pick(item, 'action', 'verb') ?? pick(container, 'action', 'verb') ?? item.action ?? null;

    normalized.target_entity = pick(item, 'target_entity', 'target', 'entity', 'targetType') ?? pick(container, 'target_entity', 'target', 'entity') ?? item.target_entity ?? null;
    normalized.target_id = pick(item, 'target_id', 'targetId', 'id') ?? pick(container, 'target_id', 'targetId', 'id') ?? item.target_id ?? item.id ?? null;
    normalized.target_name = pick(item, 'target_name') ?? pick(container, 'target_name') ?? null;

    normalized.before =
      pick(item, 'before', 'old', 'previous', 'previous_state', 'before_state') ??
      pick(container, 'before', 'old', 'previous', 'previous_state', 'before_state') ??
      item.before ??
      null;

    normalized.after =
      pick(item, 'after', 'new', 'current', 'after_state', 'changes') ??
      pick(container, 'after', 'new', 'current', 'after_state', 'changes') ??
      item.after ??
      null;

    normalized.user_agent = pick(item, 'user_agent') ?? pick(container, 'user_agent') ?? null;

    return normalized as AuditLogItem;
  });
  const meta = body?.data?.meta ?? body?.meta ?? null;
  return { logs: Array.isArray(logs) ? logs : [], meta };
}

/**
 * Export audit logs as CSV. Returns the response blob.
 * GET /adminportal/audit-log?date_from=...&date_to=...&actor=...&entity=...&export=csv
 */
export async function exportAuditLogs(from?: string, to?: string, actor?: string, entity?: string): Promise<Blob> {
  const params = new URLSearchParams();
  if (from) params.append('date_from', from);
  if (to) params.append('date_to', to);
  if (actor) params.append('actor', actor);
  if (entity) params.append('entity', entity);
  params.append('export', 'csv');
  const qs = params.toString();
  const url = `${HOSTNAME.replace(/\/$/, '')}/adminportal/audit-log${qs ? `?${qs}` : ''}`;
  const res = await authedFetch(url, { method: 'GET', headers: { ...getAuthHeaders() } });
  if (!res.ok) {
    const txt = await res.text().catch(() => '');
    const msg = `Failed to export audit logs: ${res.status} ${txt}`;
    notifyServiceError(msg);
    throw new Error(msg);
  }
  const blob = await res.blob();
  return blob;
}
